import * as React from "react";
import { Selection, type IObjectWithKey, type IColumn } from "@fluentui/react";
import { omit } from "ramda";
import { action, computed, makeObservable } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import type { ShockProfileSortKey, ShockProfile } from "../../api";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import {
    type ShockProfilesApiResource,
    type ShockProfilesQuery,
} from "../../domain/repositories/repository-shock-profiles";
import { ListShockProfileItemCell } from "../atoms/list-shock-profile-item-cell";
import type { SelectionState } from "../../utils/selection-state";
import { defaultPageSize } from "../../utils/constants";

export interface ListShockProfilesProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListShockProfileItem extends IObjectWithKey {
    readonly key: UUID;
}

import { store } from "../../store";

@observer
export class ListShockProfiles extends React.Component<ListShockProfilesProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    constructor(props: ListShockProfilesProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.shockProfiles.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<ShockProfileSortKey> {
        return store.serviceListStates.shockProfiles.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<ShockProfilesApiResource> {
        return store.serviceListStates.shockProfiles.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.shockProfiles.getSelectionState(this.listStateId);
    }

    @computed private get query(): ShockProfilesQuery {
        return { pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get shockProfiles(): (ShockProfile | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryShockProfiles.byId(id));
        }
        return store.repositoryShockProfiles.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListShockProfileItem | undefined)[] {
        return this.shockProfiles.map((shockProfile) => {
            if (!shockProfile) {
                return;
            }
            return {
                key: shockProfile.id,
            };
        });
    }

    private renderItem(shockProfileId: UUID, column: IColumn): JSX.Element {
        return <ListShockProfileItemCell shockProfileId={shockProfileId} column={column} />;
    }

    @computed private get columns(): IColumn[] {
        const columns = [
            {
                name: store.i18n.t("listShockProfiles.column.label.name"),
                key: "label",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                name: store.i18n.t("listShockProfiles.column.light.name"),
                key: "light",
                minWidth: 90,
                maxWidth: 90,
            },
            {
                name: store.i18n.t("listShockProfiles.column.medium.name"),
                key: "medium",
                minWidth: 90,
                maxWidth: 90,
            },
            {
                name: store.i18n.t("listShockProfiles.column.severe.name"),
                key: "severe",
                minWidth: 90,
                maxWidth: 90,
            },
            {
                name: store.i18n.t("listShockProfiles.column.duration.name"),
                key: "duration",
                minWidth: 90,
                maxWidth: 200,
            },
            {
                name: store.i18n.t("listShockProfiles.column.xAxisScale.name"),
                key: "xAxisScale",
                minWidth: 90,
                maxWidth: 90,
            },
            {
                name: store.i18n.t("listShockProfiles.column.yAxisScale.name"),
                key: "yAxisScale",
                minWidth: 90,
                maxWidth: 90,
            },
            {
                name: store.i18n.t("listShockProfiles.column.zAxisScale.name"),
                key: "zAxisScale",
                minWidth: 90,
                maxWidth: 90,
            },
        ];

        return this.sortState.patchColumns(columns);
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.shockProfiles.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList
                paginationState={this.paginationState}
                commandBar={this.props.commandBar ? this.props.commandBar : undefined}
            >
                <ElofleetList
                    {...props}
                    selection={this.selection}
                    onRenderItemColumn={(item: ListShockProfileItem, _index, column) =>
                        this.renderItem(item.key, column!)
                    }
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.columns}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
